<template>
  <div class="text-text-md leading-[1.428571] text-gray-900">
    <div v-if="fetching" role="status" :class="BOOTSTRAP_CONTAINER" class="flex items-center pt-5 text-text-2xl text-gray-700">
      <ProgressSpinner stroke-width="8" class="!ml-0 !mr-2 h-5 w-5 stroke-gray-700" aria-hidden="true" />Loading...
    </div>
    <div v-else-if="fetchError" :class="BOOTSTRAP_CONTAINER">
      <h1 class="mb-2.5 mt-5 text-display-lg font-medium leading-[1.1]">We couldn't find this page</h1>
      <div class="mb-5 text-text-2xl">Contact support with how you got here, along with the message below:</div>
      <div role="alert" class="mb-5 rounded-xs border border-red-100 bg-red-100 p-[15px] text-red-300">{{ fetchError }}</div>
      <div class="mb-5 text-text-2xl">
        <a :href="goalbookUrls.toolkitHome" class="text-blue-400 hover:text-blue-500 hover:underline">Return to Goalbook Toolkit</a>
      </div>
    </div>
    <CopyTrackWrapper
      v-if="collection"
      type="curated_collection"
      :title="collection.name"
      :uuid="id"
      gate-copy
      :enforce-quota="false"
    >
      <CollectionDetail :collection="collection" />
    </CopyTrackWrapper>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import ProgressSpinner from 'primevue/progressspinner'
import CopyTrackWrapper from '@/components/CopyTrackWrapper.vue'
import { useUserFeedbackContext } from '@/components/useUserFeedbackContext'
import { useUserStore } from '@/authentication/user.store'
import { getAnalyticsTracker } from '@/tracking/analytics/plugin.ts'
import { EVENT_NAMES } from '@/tracking/analytics/event_names.ts'
import { goalbookUrls } from '@/util/links'
import { setTitle } from '@/util/title'
import CollectionDetail from './CollectionDetail.vue'
import { getCuratedCollection } from './api'
import { BOOTSTRAP_CONTAINER } from './bootstrap_container'
import type { CuratedCollectionDetail } from './curated_collection_types'

const props = defineProps<{ id: string }>()

const userStore = useUserStore()
const { updateContext: updateFeedbackContext } = useUserFeedbackContext()

const fetching = ref(true)
const fetchError = ref('')
const collection = ref<CuratedCollectionDetail | null>(null)

const analytics = getAnalyticsTracker().getChildContext({
  curated_collection_uuid: computed(() => collection.value?.uuid),
  curated_collection_name: computed(() => collection.value?.name)
})

let latestFetch = 0

async function fetchCollection(id: string) {
  const thisFetch = ++latestFetch
  fetching.value = true
  fetchError.value = ''
  collection.value = null
  try {
    const loaded = await getCuratedCollection(id, userStore.userId)
    if (thisFetch !== latestFetch) return
    collection.value = loaded
    setTitle(loaded.name)
    updateFeedbackContext({ uuid: id, title: loaded.name })
  } catch (error) {
    if (thisFetch !== latestFetch) return
    fetchError.value = (error as Error).message
  }
  fetching.value = false
  analytics.sendEvent(EVENT_NAMES.CURATED_COLLECTION.VIEWED)
}

watch(() => props.id, fetchCollection, { immediate: true })
</script>
